// BANCA_v2 — proposte di abbinamento dei movimenti della banca
// Funzioni pure: ricevono i dati e restituiscono le proposte, senza scrivere niente.

export const CATEGORIE_BANCA = [
  "Incasso da cliente", "Altra entrata",
  "Materiali cantiere", "Lavorazioni esterne e subappalti", "Noli e attrezzature", "Carburante",
  "Pedaggi e Telepass", "Manutenzione mezzi", "Assicurazioni", "Utenze (luce, gas, acqua)",
  "Telefono e internet", "Affitto e locali", "Consulenze (commercialista, lavoro, tecnici)",
  "Software e abbonamenti", "Sicurezza e formazione", "Vigilanza e servizi", "Rifiuti e smaltimenti",
  "Spese bancarie", "Stipendi (netto in busta)", "Compenso titolare", "F24 contributi e ritenute", "Cassa edile",
  "Tasse e imposte", "IVA versata (non è un costo)", "Rata mutuo",
  "Ritenute sui bonifici (credito)", "Soci: prelievo o restituzione (non è un costo)", "Giroconto (non è un costo)", "Altro",
];

const STOP = new Set(("SRL SRLS SPA SNC SAS SS SOC SOCIETA COOP UNIPERSONALE DI DEL DELLA DELLE DEI DEGLI E ED IL LA LO LE GLI " +
  "IT ITALIA ITALIANA SUCCURSALE EU CONDOMINIO CASA CANTIERE VIA PIAZZA AMM AMMINISTRAZIONE " +
  "PISTOIA LUCCA PRATO FIRENZE AGLIANA MONTECATINI QUARRATA SERRAVALLE PI STOIA IA").split(" "));
export function parole(s) {
  return String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase()
    .replace(/&/g, " ").replace(/[^A-Z0-9]+/g, " ").split(" ")
    .filter(w => w.length >= 2 && !STOP.has(w) && !/^\d{1,2}$/.test(w));
}
// quanto due nomi si somigliano: 0..1 (parole in comune sul nome più corto)
export function somiglia(a, b) {
  const A = Array.from(new Set(parole(a))), B = Array.from(new Set(parole(b)));
  if (!A.length || !B.length) return 0;
  const comuni = A.filter(x => B.includes(x)).length;
  return comuni / Math.min(A.length, B.length);
}
function jaccard(a, b) {
  const A = new Set(parole(a)), B = new Set(parole(b));
  if (!A.size || !B.size) return 0;
  let c = 0; A.forEach(x => { if (B.has(x)) c++; });
  return c / (A.size + B.size - c);
}
export function chiaveControparte(s) { return parole(s).join(" "); }
const giorni = (a, b) => Math.round((Date.parse(a) - Date.parse(b)) / 86400000);
export function iso(s) {
  const t = String(s || "").trim();
  let m = t.match(/^(\d{4})-(\d{2})-(\d{2})/); if (m) return m[1] + "-" + m[2] + "-" + m[3];
  m = t.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
  if (m) { const y = m[3].length === 2 ? "20" + m[3] : m[3]; return y + "-" + m[2].padStart(2, "0") + "-" + m[1].padStart(2, "0"); }
  return "";
}
const n2 = v => Math.round((Number(v) || 0) * 100) / 100;
const uguale = (a, b) => Math.abs(n2(a) - n2(b)) <= 0.011;
const euro = n => "€ " + Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dIt = s => /^\d{4}-\d{2}-\d{2}/.test(s || "") ? s.slice(8, 10) + "/" + s.slice(5, 7) + "/" + s.slice(0, 4) : (s || "");
const MESI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const nomeMese = m => /^\d{4}-\d{2}$/.test(m || "") ? MESI[parseInt(m.slice(5, 7), 10) - 1] + " " + m.slice(0, 4) : "";
export function lordoPag(p) {
  if (Number(p.importoLordo) > 0) return n2(p.importoLordo);
  return n2((Number(p.importo) || 0) * (1 + (Number(p.iva) || 0) / 100));
}
const isBusta = d => d.categoria === "Stipendi (netto in busta)" && /busta/i.test(d.tipo || "");
const PERSONALE = ["Stipendi (netto in busta)", "F24 contributi e ritenute", "Cassa edile"];

const FISSE = {
  "Commissioni e canoni": "Spese bancarie",
  "Interessi e competenze": "Spese bancarie",
  "Imposta di bollo": "Tasse e imposte",
  "Rata mutuo": "Rata mutuo",
  "Ritenuta sui bonifici dei clienti": "Ritenute sui bonifici (credito)",
};

// ctx: { documenti, pagamenti, cantieri, lavoratori, regole, movimenti }
export function preparaContesto(ctx) {
  const docUsati = {}, pagUsati = {};
  (ctx.movimenti || []).forEach(m => {
    if (m.abb && m.abb.tipo === "documento" && m.abb.id) docUsati[m.abb.id] = m.id;
    if (m.abb && /agamento/.test(m.abb.tipo || "") && m.abb.id) pagUsati[m.abb.id] = m.id;
    if (m.abb && m.abb.pagamentoId) pagUsati[m.abb.pagamentoId] = m.id; // VENDITE_v1
  });
  return { ...ctx, docUsati, pagUsati };
}

// Proposte per un movimento: { sicura: abb|null, candidati: [{ abb, testo, motivo }], nota }
export function proponi(m, ctx) {
  const out = { sicura: null, candidati: [], nota: "" };
  const add = (abb, testo, motivo, sicura) => { out.candidati.push({ abb: { ...abb, etichetta: testo }, testo, motivo }); if (sicura && !out.sicura) out.sicura = { ...abb, etichetta: testo, auto: true }; };
  const importo = Math.abs(n2(m.importo));
  const regola = (ctx.regole || {})[chiaveControparte(m.controparte)];

  if (FISSE[m.tipo]) { add({ tipo: "categoria", categoria: FISSE[m.tipo] }, FISSE[m.tipo], "dal tipo di movimento", true); return out; }

  if (m.importo > 0) {
    // incasso: pagamento già segnato nel cantiere con lo stesso importo (lordo)
    const testo = (m.controparte || "") + " " + (m.causale || "");
    const cands = (ctx.pagamenti || []).filter(p => !/contant/i.test(p.metodo || p.tipo || "") && !(ctx.pagUsati[p.id || p._id] && ctx.pagUsati[p.id || p._id] !== m.id) && uguale(lordoPag(p), importo))
      .map(p => {
        const c = (ctx.cantieri || []).find(x => (x._id || x.id) === p.cantiereId) || {};
        const sim = Math.max(somiglia(m.controparte, p.cliente || c.cliente), somiglia(testo, p.cantiere || c.nome));
        const dd = iso(p.data) ? Math.abs(giorni(iso(p.data), m.data)) : 999;
        return { p, c, sim, dd };
      }).filter(x => x.sim >= 0.5 || x.dd <= 10).sort((a, b) => b.sim - a.sim || a.dd - b.dd);
    cands.forEach((x, i) => {
      const ok = x.sim >= 0.5 && x.dd <= 20 && cands.length === 1;
      add({ tipo: "pagamento", id: x.p.id || x.p._id }, "Incasso " + (x.p.cantiere || x.c.nome || "") + " (" + (x.p.tipo || "") + ")",
        "stesso importo" + (x.sim >= 0.5 ? " e cliente" : "") + (x.dd ? ", segnato il " + dIt(iso(x.p.data)) + (x.dd > 20 ? " (" + x.dd + " giorni di differenza)" : "") : ""), ok && i === 0);
    });
    // nessun pagamento: proposta di registrarlo nel cantiere più somigliante
    const best = (ctx.cantieri || []).map(c => ({ c, s: Math.max(somiglia(m.controparte, c.cliente), somiglia(testo, c.nome)) + jaccard(testo, c.nome) * 0.01 + (c.completato ? 0 : 0.005) }))
      .filter(x => x.s >= 0.5).sort((a, b) => b.s - a.s);
    out.cantiereProposto = best.length ? (best[0].c._id || best[0].c.id) : "";
    if (!cands.length) out.nota = best.length ? "Incasso non ancora segnato: cantiere probabile " + best[0].c.nome : "Incasso non segnato in nessun cantiere";
    if (regola) add({ tipo: "categoria", categoria: regola }, regola, "regola salvata", !out.sicura && !cands.length && !best.length);
    return out;
  }

  // uscite
  const docs = (ctx.documenti || []).filter(d => !(ctx.docUsati[d.id] && ctx.docUsati[d.id] !== m.id && !isBusta(d) && (d.scadenze || []).length <= 1));
  if (m.tipo === "Stipendio") {
    // senza mese nella causale: busta del mese del bonifico o di quello prima
    const meseOk = d => m.mese ? d.mese === m.mese : (d.mese && Math.abs(giorni(d.mese + "-15", m.data)) <= 45);
    const buste = docs.filter(d => isBusta(d) && somiglia(m.controparte, d.lavoratoreNome || d.fornitore) >= 0.5 && meseOk(d));
    buste.forEach(d => add({ tipo: "documento", id: d.id }, "Busta paga " + (d.lavoratoreNome || d.fornitore) + " " + nomeMese(d.mese), "lavoratore e mese", buste.length === 1));
    const lav = (ctx.lavoratori || []).find(l => somiglia(m.controparte, l.nome) >= 0.99);
    if (!buste.length && lav) {
      const tit = /bertocci/i.test(lav.nome) ;
      add({ tipo: "categoria", categoria: tit ? "Compenso titolare" : "Stipendi (netto in busta)" }, (tit ? "Compenso titolare " : "Stipendio ") + lav.nome + (m.mese ? " " + nomeMese(m.mese) : ""), "busta paga non ancora caricata", !regola);
    }
  } else if (m.tipo === "F24" || m.tipo === "Cassa edile") {
    const f = docs.filter(d => (m.tipo === "F24" ? /F24/i.test(d.tipo || "") : d.categoria === "Cassa edile") && (uguale(d.totale, importo) || (d.scadenze || []).some(s => uguale(s.importo, importo))) && Math.abs(giorni(d.data || m.data, m.data)) <= (m.tipo === "F24" ? 7 : 60));
    f.forEach(d => add({ tipo: "documento", id: d.id }, d.tipo + " " + (d.descrizione || d.fornitore || "") + " " + euro(d.totale), "stesso importo", f.length === 1));
    if (!f.length) {
      if (m.tipo === "Cassa edile") add({ tipo: "categoria", categoria: "Cassa edile" }, "Cassa edile" + (m.mese ? " " + nomeMese(m.mese) : ""), "documento non ancora caricato", true);
      else out.nota = "F24 non ancora caricato nella cassetta";
    }
  } else {
    const testo = (m.controparte || "") + " " + (m.causale || "");
    // numero di fattura scritto nella causale (es. "FT.N.RVFV0048410", "fattura 21"): se c'è e non è quello del documento, il documento è un altro
    const numCausale = ((m.causale || m.testo || "").match(/\b(?:FT|FATT|FATTURA)\.?\s*(?:N\.?|NR\.?)?\s*([A-Z0-9][A-Z0-9/-]*\d)/i) || [])[1] || "";
    const f = docs.filter(d => !PERSONALE.includes(d.categoria) && d.data && giorni(d.data, m.data) <= 5 && giorni(m.data, d.data) <= 240
        && (uguale(Math.abs(Number(d.totale) || 0), importo) || (d.scadenze || []).some(s => uguale(Math.abs(Number(s.importo) || 0), importo))))
      .map(d => {
        const num = String(d.numero || "").replace(/^0+/, "");
        const conNum = num && new RegExp("(FATT|FT|FATTURA|N)[^0-9]{0,6}0*" + num.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&") + "\\b", "i").test(m.causale || m.testo || "");
        const altroNum = !conNum && numCausale && /[A-Z]/i.test(numCausale) && num && numCausale.toUpperCase().replace(/^0+/, "") !== num.toUpperCase();
        return { d, sim: somiglia(testo, d.fornitore), conNum, altroNum, dd: giorni(m.data, d.data) };
      }).filter(x => !x.altroNum && (x.sim >= 0.5 || x.conNum || m.tipo === "Carta (POS)" && x.sim > 0)).sort((a, b) => (b.sim + (b.conNum ? 1 : 0)) - (a.sim + (a.conNum ? 1 : 0)));
    f.forEach((x, i) => add({ tipo: "documento", id: x.d.id }, (x.d.tipo || "Fattura") + " " + x.d.fornitore + (x.d.numero ? " n. " + x.d.numero : "") + " del " + dIt(x.d.data),
      "stesso importo" + (x.sim >= 0.5 ? " e fornitore" : "") + (x.conNum ? ", numero nella causale" : "") + (x.dd > 75 ? ", ma la fattura è di " + x.dd + " giorni prima" : ""),
      i === 0 && x.sim >= 0.5 && (x.conNum || (f.length === 1 && x.dd <= 75))));
    if (!f.length) out.nota = m.tipo === "PagoPA / CBILL" ? "" : "Fattura non trovata nella cassetta";
    if (m.tipo === "PagoPA / CBILL" && /RISCOSSIONE/i.test(m.controparte || m.testo)) add({ tipo: "categoria", categoria: "Tasse e imposte" }, "Tasse e imposte (cartelle)", "Agenzia Entrate-Riscossione", false);
  }
  if (regola) add({ tipo: "categoria", categoria: regola }, regola, "regola salvata per " + m.controparte, !out.sicura);
  return out;
}
